import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import type { Invitation } from "@/pages/admin/invitations/components/InvitationModal";

interface Props {
  form: Invitation;
  onChange: <K extends keyof Invitation>(key: K, val: Invitation[K]) => void;
}

type InvTemplate = {
  id: string;
  name: string;
  preview_url: string | null;
  background_url: string | null;
  text_color: string;
};

/* ─── Theme definitions (must match public THEMES map) ── */
const THEMES: {
  key: string;
  label: string;
  description: string;
  palette: string[];
  preview: string;
}[] = [
  {
    key: "classic",
    label: "Classic Elegante",
    description: "Marfil y tonos dorados. Perfecto para bodas y eventos formales.",
    palette: ["#FAF7F2", "#B8935A", "#7A6040", "#2C1F0E"],
    preview: "bg-gradient-to-br from-amber-50 to-stone-100",
  },
  {
    key: "modern",
    label: "Modern Minimal",
    description: "Blanco limpio con acentos carbón. Contemporáneo y sofisticado.",
    palette: ["#F8F8FA", "#1A1A2E", "#6B6B80", "#E4E4EE"],
    preview: "bg-gradient-to-br from-white to-slate-100",
  },
  {
    key: "floral",
    label: "Floral Garden",
    description: "Rosas suaves y verdes. Romántico y fresco.",
    palette: ["#FDF6F8", "#C2607A", "#8B5060", "#F0D0D8"],
    preview: "bg-gradient-to-br from-pink-50 to-rose-50",
  },
  {
    key: "gold",
    label: "Gold Luxury",
    description: "Dorado y crema. Elegante y lujoso.",
    palette: ["#FAF8F2", "#D4A850", "#8B8050", "#E8E0C8"],
    preview: "bg-gradient-to-br from-yellow-50 to-amber-50",
  },
  {
    key: "sage",
    label: "Sage Garden",
    description: "Verde salvia y tonos naturales. Sereno y orgánico.",
    palette: ["#F5F7F3", "#5E7D60", "#4F6B50", "#D0DDD0"],
    preview: "bg-gradient-to-br from-green-50 to-emerald-50",
  },
  {
    key: "dark",
    label: "Dark Luxury",
    description: "Azul profundo y dorado. Dramático y sofisticado.",
    palette: ["#0F172A", "#C8A96E", "#1E293B", "#334155"],
    preview: "bg-gradient-to-br from-slate-900 to-slate-800",
  },
  {
    key: "celestial",
    label: "Celestial",
    description: "Azul medianoche con toques plateados. Mágico y etéreo.",
    palette: ["#1E1B4B", "#A5B4FC", "#C7D2FE", "#3730A3"],
    preview: "bg-gradient-to-br from-indigo-950 to-violet-900",
  },
];

const DESIGN_MODES = [
  { key: "template",        label: "Template Genérico",     desc: "Usa un fondo del catálogo del estudio",  icon: "ri-palette-line" },
  { key: "photo",           label: "Foto de Portada",        desc: "La foto de portada como fondo",          icon: "ri-image-line" },
  { key: "custom_template", label: "Template Personalizado", desc: "Template exclusivo asignado al cliente", icon: "ri-vip-crown-line" },
];

export default function DesignTab({ form, onChange }: Props) {
  const [templates, setTemplates] = useState<InvTemplate[]>([]);
  const [loadingTpl, setLoadingTpl] = useState(false);

  const selected = THEMES.find((t) => t.key === form.theme_key) ?? THEMES[0];
  const currentMode = form.design_mode || "template";

  // Load templates when mode = template
  useEffect(() => {
    if (currentMode !== "template") return;
    const load = async () => {
      setLoadingTpl(true);
      const tid = await getTenantId();
      const { data } = await supabase
        .from("invitation_templates")
        .select("id, name, preview_url, background_url, text_color")
        .or(tid ? `tenant_id.eq.${tid},tenant_id.is.null` : "tenant_id.is.null")
        .eq("is_active", true)
        .order("sort_order");
      setTemplates((data ?? []) as InvTemplate[]);
      setLoadingTpl(false);
    };
    load();
  }, [currentMode]);

  return (
    <div className="space-y-7">

      {/* ── 1. Design mode ── */}
      <div>
        <h3 className="text-slate-800 font-semibold text-sm mb-1">Modo de Diseño</h3>
        <p className="text-slate-400 text-xs mb-3">Define cómo se mostrará el fondo de la invitación pública.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {DESIGN_MODES.map((m) => {
            const active = currentMode === m.key;
            return (
              <button
                key={m.key}
                onClick={() => onChange("design_mode", m.key)}
                className={`rounded-xl border p-4 text-left transition-all cursor-pointer ${active ? "border-indigo-400 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${active ? "bg-indigo-100 text-indigo-600" : "bg-slate-50 text-slate-400"}`}>
                  <i className={m.icon} />
                </div>
                <p className={`text-sm font-semibold ${active ? "text-indigo-700" : "text-slate-700"}`}>{m.label}</p>
                <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">{m.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── 2. Template catalog (when mode=template) ── */}
      {currentMode === "template" && (
        <div>
          <h3 className="text-slate-800 font-semibold text-sm mb-1">Fondo del Catálogo</h3>
          <p className="text-slate-400 text-xs mb-3">Selecciona un template del catálogo del estudio o global.</p>
          {loadingTpl ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
              <div className="w-4 h-4 border-2 border-slate-300 border-t-indigo-400 rounded-full animate-spin" />
              Cargando templates…
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-slate-200 rounded-xl">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-slate-50 flex items-center justify-center text-slate-300">
                <i className="ri-image-line text-xl" />
              </div>
              <p className="text-slate-500 text-sm">No hay templates activos.</p>
              <p className="text-slate-400 text-xs mt-1">
                Ve a <strong>Invitations → Templates</strong> para agregar fondos genéricos.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {templates.map((tpl) => {
                const thumb = tpl.preview_url || tpl.background_url;
                const active = form.template_id === tpl.id;
                return (
                  <button
                    key={tpl.id}
                    onClick={() => {
                      onChange("template_id", tpl.id);
                      if (tpl.text_color) onChange("text_color", tpl.text_color);
                    }}
                    className={`rounded-xl border overflow-hidden text-left transition-all cursor-pointer ${active ? "border-indigo-400 ring-2 ring-indigo-100" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <div className="relative h-20 bg-slate-100">
                      {thumb
                        ? <img src={thumb} alt={tpl.name} className="w-full h-full object-cover object-top" />
                        : <div className="w-full h-full flex items-center justify-center text-slate-300"><i className="ri-image-line text-2xl" /></div>
                      }
                      {active && (
                        <div className="absolute inset-0 flex items-center justify-center bg-indigo-500/20">
                          <div className="w-7 h-7 rounded-full bg-indigo-500 flex items-center justify-center">
                            <i className="ri-check-line text-white text-xs" />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-2.5">
                      <p className={`text-xs font-medium truncate ${active ? "text-indigo-700" : "text-slate-700"}`}>{tpl.name}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── 3. Theme (color scheme) ── */}
      <div>
        <h3 className="text-slate-800 font-semibold text-sm mb-1">Esquema de Colores</h3>
        <p className="text-slate-400 text-xs mb-3">
          {currentMode === "template"
            ? "El esquema de colores afecta los elementos de texto cuando el template no tiene imagen de fondo."
            : "Define la paleta de colores de la invitación pública."}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {THEMES.map((theme) => {
            const isActive = form.theme_key === theme.key;
            const isDark = theme.key === "dark" || theme.key === "celestial";
            return (
              <button
                key={theme.key}
                onClick={() => onChange("theme_key", theme.key)}
                className={`text-left rounded-xl border-2 overflow-hidden transition-all cursor-pointer ${
                  isActive ? "border-indigo-400 ring-2 ring-indigo-100" : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className={`h-16 ${theme.preview} relative flex items-center justify-center`}>
                  <span className={`text-xs font-light tracking-widest uppercase ${isDark ? "text-white/50" : "text-slate-400/60"}`}>
                    Preview
                  </span>
                  {isActive && (
                    <div className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center bg-indigo-500 rounded-full">
                      <i className="ri-check-line text-white text-xs" />
                    </div>
                  )}
                </div>
                <div className="p-3 bg-white">
                  <div className="flex items-center justify-between mb-1">
                    <p className={`text-sm font-semibold ${isActive ? "text-indigo-700" : "text-slate-700"}`}>
                      {theme.label}
                    </p>
                    <div className="flex items-center gap-0.5">
                      {theme.palette.slice(0, 3).map((color) => (
                        <div key={color} className="w-3 h-3 rounded-full border border-white/50" style={{ backgroundColor: color }} />
                      ))}
                    </div>
                  </div>
                  <p className="text-slate-400 text-xs leading-relaxed">{theme.description}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Selected summary */}
        <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${selected.preview} shrink-0`} />
          <div>
            <p className="text-slate-700 font-semibold text-sm">{selected.label}</p>
            <p className="text-slate-400 text-xs mt-0.5">{selected.description}</p>
            <div className="flex items-center gap-1.5 mt-2">
              {selected.palette.map((color) => (
                <div key={color} className="w-4 h-4 rounded-full border border-slate-200" style={{ backgroundColor: color }} title={color} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── 4. Text color override ── */}
      <div>
        <h3 className="text-slate-800 font-semibold text-sm mb-1">Color de Texto</h3>
        <p className="text-slate-400 text-xs mb-3">Color del texto sobre el fondo en la vista pública. Solo aplica cuando hay imagen de fondo.</p>
        <div className="flex items-center gap-4">
          <input
            type="color"
            value={form.text_color || "#ffffff"}
            onChange={(e) => onChange("text_color", e.target.value)}
            className="w-12 h-10 rounded-lg border border-slate-200 cursor-pointer overflow-hidden"
          />
          <div className="flex gap-2 flex-wrap">
            {["#ffffff", "#000000", "#FFF8F0", "#F3F0E8", "#1A1A1A"].map((c) => (
              <button
                key={c}
                onClick={() => onChange("text_color", c)}
                title={c}
                className="w-8 h-8 rounded-lg border-2 cursor-pointer transition-all"
                style={{
                  backgroundColor: c,
                  borderColor: form.text_color === c ? "#6366f1" : "#e2e8f0",
                }}
              />
            ))}
          </div>
          <span className="text-xs text-slate-400 font-mono">{form.text_color || "#ffffff"}</span>
        </div>
      </div>
    </div>
  );
}